import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContactView } from '@api-proxies';
import { ActivityLogGridComponent } from '@components/shared/activity-log-grid/activity-log-grid.component';
import { ContactService } from '@http/clients/contact.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { TabCounterService, TabCounterType } from '@services/tab-counter.service';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { NotesComponent } from '@shared/notes/notes.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { getTab, processTabParameter } from '@utils/tab-navigation';
import {
    CounterBadgeComponent,
    ModalService,
    StatusIndicatorComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { Subscription } from 'rxjs';
import { ContactsSingleBuildingsComponent } from './contacts-single-buildings/contacts-single-buildings.component';
import { ContactsSingleOpportunitiesComponent } from './contacts-single-opportunities/contacts-single-opportunities.component';
import { ContactsSingleOverviewComponent } from './contacts-single-overview/contacts-single-overview.component';

@Component({
    selector: 'app-contacts-single',
    templateUrl: './contacts-single.component.html',
    styleUrls: ['./contacts-single.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SectionHeaderComponent,
        StatusIndicatorComponent,
        CounterBadgeComponent,
        TabViewComponent,
        TabItemComponent,
        KebabMenuComponent,
        ContactsSingleOverviewComponent,
        ContactsSingleOpportunitiesComponent,
        ContactsSingleBuildingsComponent,
        NotesComponent,
        ActivityLogGridComponent
    ]
})
export class ContactsSingleComponent implements OnInit, OnDestroy {
    @ViewChild(TabViewComponent)
    tabControl!: TabViewComponent;

    contactId: number;

    contact?: ContactView;

    currentTab: string;

    isActive?: boolean;

    entityTypeId: EntityTypes;

    tabsCounters: TabCounterType = {
        Opportunities: 0,
        Buildings: 0,
        Notes: 0
    };

    entitySubscription?: Subscription;

    constructor(
        private readonly contactService: ContactService,
        private readonly awaiter: AwaiterService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly modalService: ModalService,
        private readonly tabCounterService: TabCounterService
    ) {
        this.contactId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.currentTab = getTab(this.route.snapshot.paramMap.get('tab')) ?? TabNames[TabNames.Overview];
        this.isActive = true;
        this.entityTypeId = EntityTypes.Contact;
    }

    async ngOnInit(): Promise<void> {
        await this.updateTabCounters();

        this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
            this.contact = entityData;
            this.isActive = this.contact?.isActive;
        });

        this.route.paramMap.subscribe(async (params) => {
            const newTab = getTab(params.get('tab'));

            if (newTab && newTab !== this.currentTab && this.tabControl) {
                this.currentTab = processTabParameter(this.route, this.tabControl);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    onEditContact() {
        this.router.navigate([`/contacts-edit/${this.contactId}`]);
    }

    async archiveContact(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Archive',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to archive this contact?',
            title: 'Archive Contact'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Archiving Contact', async () => {
            if (!this.contact?.id) return;

            await this.contactService.deactivate(this.contact?.id);
            this.isActive = false;
        });
    }

    async restoreContact(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Restore',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to restore this contact?',
            title: 'Restore Contact'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Restoring Contact', async () => {
            if (!this.contact?.id) return;
            await this.contactService.activate(this.contact.id);
            this.isActive = true;
        });
    }

    async onTabCountersChange(): Promise<void> {
        await this.updateTabCounters();
    }

    tabChange(tabName: string) {
        this.router.navigate(['/contacts-single', this.contactId, tabName]);
    }

    private async updateTabCounters(): Promise<void> {
        await this.tabCounterService.updateCounters(this.entityTypeId, this.contactId, this.tabsCounters);
    }
}
